interface StaticPageSEO {
  title: string;
  description: string;
  keywords: string;
  [key: string]: string;
}

interface DynamicPageSEO {
  title: string;
  description: string;
  keywords: string;
  [key: string]: string;
}

interface StaticPages {
  [key: string]: StaticPageSEO;
  home: StaticPageSEO;
  about: StaticPageSEO;
  contact: StaticPageSEO;
  resources: StaticPageSEO;
  faq: StaticPageSEO;
}

interface DynamicPages {
  [key: string]: DynamicPageSEO;
  pincode: DynamicPageSEO;
  area: DynamicPageSEO;
  district: DynamicPageSEO;
  state: DynamicPageSEO;
}

interface SEOConfig {
  baseUrl: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string;
  siteName: string;
  twitterHandle: string;
  defaultImage: string;
  pages: StaticPages;
  templates: DynamicPages;
}

export const seoConfig: SEOConfig = {
  baseUrl: 'https://pincodepro.tag360.in',
  defaultTitle: 'Pincode Pro - India\'s Leading Pincode Search Service',
  defaultDescription: 'Find accurate pincode information for any location in India. Search by area, post office, or pincode. Updated daily with official India Post data.',
  defaultKeywords: 'pincode, india post, postal code, zip code, area search, post office finder',
  siteName: 'Pincode Pro',
  twitterHandle: '@pincodepro',
  defaultImage: 'https://pincodepro.tag360.in/images/og-image.jpg',

  // Page specific configurations
  pages: {
    home: {
      title: 'Pincode Pro - Find Any Pincode in India | Official Postal Code Directory',
      description: 'India\'s most comprehensive pincode directory. Search 700,000+ locations, find post offices, and verify addresses with official India Post data.',
      keywords: 'india pincode search, post office finder, area pincode lookup, postal code directory',
    },
    about: {
      title: 'About Pincode Pro | India\'s Trusted Pincode Directory',
      description: 'Learn about Pincode Pro\'s mission to provide accurate and up-to-date postal information for every location in India.',
      keywords: 'about pincode pro, india post directory, postal information service',
    },
    contact: {
      title: 'Contact Pincode Pro | Get Support & Information',
      description: 'Get in touch with Pincode Pro for support, feedback, or business inquiries. We\'re here to help with all your postal code needs.',
      keywords: 'contact pincode pro, support, help, feedback, business inquiries',
    },
    resources: {
      title: 'Pincode Resources & Guides | Pincode Pro',
      description: 'Access comprehensive guides, tools, and resources for understanding and using Indian postal codes effectively.',
      keywords: 'pincode guides, postal resources, india post information, pincode tools',
    },
    faq: {
      title: 'Frequently Asked Questions | Pincode Pro Help Center',
      description: 'Find answers to common questions about Indian pincodes, post offices, and postal services. Updated regularly with user questions.',
      keywords: 'pincode FAQ, postal code questions, india post help, pincode queries',
    },
  },

  // Dynamic page templates
  templates: {
    pincode: {
      title: '{pincode} - Post Offices & Areas | Pincode Pro',
      description: 'Find all post offices and areas under pincode {pincode}. Get complete postal information, maps, and nearby services.',
      keywords: '{pincode}, post office {pincode}, areas in {pincode}, {mainArea} pincode',
    },
    area: {
      title: '{areaName} Pincode - {pincode} | Complete Postal Information',
      description: 'Get the correct pincode for {areaName}. Find nearby post offices, complete address format, and postal services in {areaName}.',
      keywords: '{areaName} pincode, {areaName} postal code, {areaName} post office, {pincode}',
    },
    district: {
      title: '{district} Pincodes - Complete List of Postal Codes | Pincode Pro',
      description: 'Find all pincodes in {district}. Complete list of post offices, areas, and postal codes in {district} district.',
      keywords: '{district} pincodes, {district} postal codes, {district} post offices',
    },
    state: {
      title: '{state} Pincodes - Post Office Directory | Pincode Pro',
      description: 'Complete directory of pincodes in {state}. Search postal codes by district, city, or area in {state}.',
      keywords: '{state} pincodes, {state} postal codes, {state} post offices, {state} postal directory',
    },
  }
};

export const generateMetaTags = (pageType: string, data?: any) => {
  const config = seoConfig.pages[pageType] || seoConfig.templates[pageType];
  if (!config) return null;

  let title = config.title;
  let description = config.description;
  let keywords = config.keywords;

  // Replace placeholders with actual data for dynamic pages
  if (data) {
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{${key}}`, 'g');
      title = title.replace(regex, data[key]);
      description = description.replace(regex, data[key]);
      keywords = keywords.replace(regex, data[key]);
    });
  }

  return {
    title,
    description,
    keywords,
    ogTitle: title,
    ogDescription: description,
    ogUrl: `${seoConfig.baseUrl}${data?.url || ''}`,
    ogImage: seoConfig.defaultImage,
    twitterCard: 'summary_large_image',
    twitterSite: seoConfig.twitterHandle,
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: seoConfig.defaultImage,
  };
};

export const generateSchema = (type: string, data: any) => {
  switch (type) {
    case 'pincode':
      return {
        '@context': 'https://schema.org',
        '@type': 'PostalAddress',
        postalCode: data.pincode,
        addressRegion: data.state,
        addressCountry: 'IN',
        description: `Complete postal information for pincode ${data.pincode}`,
      };
    case 'area':
      return {
        '@context': 'https://schema.org',
        '@type': 'Place',
        name: data.areaName,
        address: {
          '@type': 'PostalAddress',
          addressLocality: data.areaName,
          postalCode: data.pincode,
          addressRegion: data.state,
          addressCountry: 'IN',
        },
      };
    case 'faq':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: data.faqs.map((faq: any) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      };
    default:
      return null;
  }
};
